import { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Chip, FormControl, FormControlLabel, FormLabel, Grid, Icon, InputAdornment, InputLabel, OutlinedInput, Radio } from '@mui/material';
import { GridColDef, GridRenderEditCellParams } from '@mui/x-data-grid';
import { Switch as FormikSwitch, TextField } from 'formik-mui';
import { Field, Form, FormikProps, FormikProvider } from 'formik';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import MuiTextField from '@mui/material/TextField';
import RadioGroup from '@mui/material/RadioGroup';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import itLocale from 'date-fns/locale/it';
import * as Yup from 'yup';

import { ILayoutContext, LayoutContext, ModuleHandle, TimezoneField, ToastSeverityEnum, useUderlyStore } from '@uderly/react-uderly-ui';
import { EntityNotifications } from '@uderly/react-uderly-ui';
// import Settings from '../../app/settings';
import ShareEvent from '../components/ShareEvent';
import EventExtraTabs from "../components/EventExtraTabs";
import Module, { DisplayCreatedAt, ModuleHostProps } from "../components/Module";
import React from 'react';
import useMediaImage from '../components/Module/hooks/useMediaImage';
import CellMediaThumb from '../components/Module/CellMediaThumb';
import ActionButton from '../components/ActionButton';
import useModuleUtils from '../components/Module/hooks/useModuleUtils';
import { StoreBreadcrumbs } from "../components/Module/StoreBreadcrumbs";

import AppContext from '../AppContext';
import IqSpaceApi from '../../api/IqSpace';
import { IqStore } from '../../models/IqStore';

interface EventsProps extends ModuleHostProps {
	storeSubdomain: string;
}

const Events = (props: EventsProps) => {
	const { t } = useTranslation();
	const layoutContext = useContext(LayoutContext) as ILayoutContext;

	const zustandStore = useUderlyStore(s => s.store);

	const [store, setStore] = useState<IqStore | null>(zustandStore as IqStore);

	useEffect(() => {
		const init = async () => {
			const response = await IqSpaceApi.Store(props.storeSubdomain);

			if (response && response.status === 200) {
				setStore(response.data.data);
			}
		}

		init();

	}, [props.storeSubdomain]);

	const moduleData = {
		title: <StoreBreadcrumbs store={store} path="/events" />,
		name: t('events'),
		path: `/${store?.subdomain}/events`,
		apiBasePath: '/events'
	};

	const moduleRef = useRef<ModuleHandle>(null);

	const user = useUderlyStore((state) => state.user);

	const [item, setItem] = useState<any>(null);

	const mediaImage = useMediaImage(item, moduleRef, moduleData);
	const moduleUtils = useModuleUtils({ data: moduleData });

	const columns: GridColDef[] = [
		{
			field: 'title',
			headerName: t('title') as string,
			minWidth: 180,
			width: 200,
			flex: 1,
			renderCell: (params: any) => {
				const { title } = params.row;

				return (
					<div className="info-icons">
						<CellMediaThumb o={params.row} path={moduleData?.apiBasePath as string} />
						{title}
					</div>
				);
			},
		},
		{
			field: 'price',
			headerName: AppContext.r["price"],
			minWidth: 60,
			sortable: false,
			renderCell: params => {
				const { row } = params;

				const p = `${store?.locale?.currency_symbol} ${row.price} ${(row.price === 0 || !row.price || row.is_free) && t('free')}`;

				return <div className="td-v-center">{p}</div>;
			}
		},
		{
			field: 'date_time',
			headerName: t('datetime') as string,
			minWidth: 160,
			renderCell: (params: any) => {
				if (!params.row.date_time) return '';

				return format(new Date(params.row.date_time), 'd/MM/Y H:mm');
			},
		},
		{
			field: 'timezone',
			headerName: t('timezone') as string,
			minWidth: 160,
		},
		{
			field: 'published',
			headerName: t('published') as string,
			minWidth: 120,
			renderCell: (params: GridRenderEditCellParams) => (
				<div className="info-icons">
					<ActionButton
						size="sm"
						action={async () =>
							await moduleUtils.onTogglePublished(params.row, moduleRef)
						}
					>
						{params.row.published ? (
							<RadioButtonCheckedIcon />
						) : (
							<RadioButtonUncheckedIcon />
						)}
					</ActionButton>
				</div>
			),
		},
	];

	const [isFree, setIsFree] = useState<boolean>(true);
	const [netIncome, setNetIncome] = useState<number | undefined>();
	const [transactionFee, setTransactionFee] = useState<number | undefined>();

	const schema = () => {
		const v = {
			title: Yup.string().min(2).required((t('required-field') as string)),
			date_time: Yup.date().required(t('required-field') as string)
		};

		if (!isFree)
			v["price"] = Yup.number()
				.required(t('required-field') as string)
				.min(minPrice, ((t('required-field') as string).replace("{currency}", store?.locale.currency_symbol)));

		return Yup.object(v);
	}

	const minPrice = 0.5;
	const defaultPrice = 5;

	const initialValues = {
		title: `IQ Space ${format(new Date(), "MM/dd")}`,
		description: '',
		date_time: format(new Date(), (new Date().getHours() > 20 && new Date().getMinutes() > 30) ? "yyyy-MM-dd HH:mm:ss" : "yyyy-MM-dd 19:00:00"),
		timezone: store?.timezone,
		published: true,
		archived: false,
		location_type_id: null,

		max_attendants_count: 1000,
		is_free: true,
		price: defaultPrice,
		netIncome: 0,
		transactionFee: 0,
		autostart: false,
		autopilot: false,
		metadata: undefined
	};

	const calculateFees = (price?: number) => {
		if (store && price) {
			const { licence } = store;

			if (licence) {
				const vatRate = 1.2;

				const fixedFee = parseFloat(licence.stripe_fixed_fee) * vatRate;
				const transactionFee = ((price) / 100 * parseFloat(licence.stripe_fee)) + fixedFee;

				setNetIncome((price) - transactionFee);
				setTransactionFee(transactionFee);
			}
		}
	}

	const onItemLoaded = (item: any) => {
		if (item && item.id) {
			setItem(item);
			setIsFree(!(item.price > 0));

			calculateFees(item.price);
		}
	};

	const warnLicenceRequired = (store) => {
		layoutContext.toast.show({
			open: true,
			severity: ToastSeverityEnum.warning,
			autoHideDuration: 7000,
			content: <>
				<p>{t("pro-licence-required")}</p>
				<Link to={"/" + store.subdomain + "/settings"}>{t('upgrade-licence', { store: store.name })}</Link>
			</>
		});
	}

	const warnEventMustBePublished = () => {
		layoutContext.toast.show({
			open: true,
			severity: ToastSeverityEnum.warning,
			autoHideDuration: 7000,
			content: <p>{t("event-must-be-published")}</p>
		});
	}

	const getMetadata = ({ values }) =>
		values.metadata ? JSON.parse(values.metadata) : undefined;

	const view = (item: any, formik: FormikProps<any>) => {
		const { values, setFieldValue } = formik;
		const defaultMetadata = { Autopilot: { Speed: 0 } };

		// const imageUrl =
		// 	Settings.apiUrl + moduleData.apiBasePath + '/' + item.id + '/image';

		if (!store) return null;

		return (
			<FormikProvider value={formik}>
				<Grid container className='event-mask'>
					<Grid container spacing={2} item sm={9}>
						<Grid item xs={10}>
							<Field
								component={TextField}
								name="title"
								type="text"
								label={t('title') + ' *'}
							/>
						</Grid>

						<Grid item xs={2}>
							<FormControlLabel
								control={
									<Field component={FormikSwitch} type="checkbox" name="published" />
								}
								label={t('published')}
							/>
						</Grid>

						<Grid item xs={12} sm={5} className="centered-item">
							{values && values.timezone &&
								<TimezoneField value={values.timezone} onChange={(e) => {
									setFieldValue("timezone", e?.ianaId)
								}} />}
						</Grid>

						<Grid item xs={9} sm={5} className="centered-item">
							<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={itLocale}>
								<DateTimePicker
									inputFormat="yyyy-MM-dd HH:mm:ss"
									value={new Date(values.date_time)}
									onChange={(v) => setFieldValue("date_time", format(v as Date, "yyyy-MM-dd HH:mm:ss"))}
									label={AppContext.r["datetime"]}
									renderInput={(params) => <MuiTextField {...params} />} />
							</LocalizationProvider>
						</Grid>

						<Grid item xs={3} sm={2} className="centered-item">
							<Button variant="contained" className="now-button" endIcon={<AccessTimeIcon />}
								onClick={() => {
									setFieldValue("date_time", format(new Date(), "yyyy-MM-dd HH:mm:ss"))
								}}>{t('now')}</Button>
						</Grid>

						{/* <Grid item xs={12}>
							<ReactQuill
								value={values.description}
								onChange={(value: string) => setFieldValue('description', value)}
							/>
						</Grid> */}

						{values.id &&
							<Grid item xs={12} sm={12}>
								<Box marginTop={2}>
									<ShareEvent event={values} label={AppContext.r["event-online-at"]} disableIcons />
								</Box>
							</Grid>}

						{values.id &&
							<Grid item xs={12}>
								<h3>{"Game Automation"}</h3>

								{!(!values.games || values.games.length === 0) &&
									<Alert severity="info">{t("game-already-started")}</Alert>}

								{(!values.games || values.games.length === 0) &&
									<table className="game-automation">
										<tbody>
											<tr>
												<td><b>{t('auto-start')}</b>{t('auto-start-desc')}</td>
												<td>
													<FormControlLabel label=""
														control={<Field component={FormikSwitch} type="checkbox" name="autostart" checked={values.autostart} />}
														onChange={(_e, checked) => {
															if (checked && store.licence?.id === 1) {
																warnLicenceRequired(store);
															} else if (checked && !values.published) {
																warnEventMustBePublished();
															} else {
																setFieldValue("autostart", checked);
															}
														}
														} />
												</td>
											</tr>

											<tr>
												<td>
													<b>{t('auto-pilot')}</b>
													{t('auto-pilot-desc')}

													{/* Autopilot settings */}
													{values.autopilot &&
														<FormControl className="game-speed">
															<FormLabel id="speed-radio-buttons-group-label">{t('speed')}</FormLabel>
															<RadioGroup
																className="speed-radio-group"
																aria-labelledby="speed-radio-buttons-group-label"
																defaultValue={values.metadata ? JSON.parse(values.metadata).Autopilot?.Speed : 0}
																onChange={(_e, value) => {
																	const metadata = values.metadata ? JSON.parse(values.metadata) : defaultMetadata;

																	if (!metadata.Autopilot)
																		metadata.Autopilot = { Speed: parseInt(value) }
																	else
																		metadata.Autopilot.Speed = parseInt(value);

																	setFieldValue("metadata", JSON.stringify(metadata));
																}}>
																<FormControlLabel value={-1} control={<Radio />} label={t('slow')} />
																<FormControlLabel value={0} control={<Radio />} label={t('normal')} />
																<FormControlLabel value={1} control={<Radio />} label={t('fast')} />
															</RadioGroup>
														</FormControl>}
												</td>

												<td>
													<FormControlLabel label=""
														control={<Field component={FormikSwitch} type="checkbox" name="autopilot" checked={values.autopilot} />}
														onChange={(_e, checked) => {
															if (checked && store.licence?.id === 1) {
																warnLicenceRequired(store);
															} else if (checked && !values.published) {
																warnEventMustBePublished();
															} else {
																setFieldValue("autopilot", checked);
															}
														}
														} />
												</td>
											</tr>
										</tbody>
									</table>}

								{store.licence?.id === 1 ? <>
									<h3>{t('notifications')}</h3>
									<p>{t("pro-licence-required")}</p>
								</> : <EntityNotifications values={values} />}
							</Grid>}
					</Grid>

					<Grid item xs={12} sm={3} padding={1} className='media-image-container'>
						{mediaImage.renderComponent(formik, { apiPath: "/events" })}
					</Grid>

					{/* Tickets */}

					{values.id &&
						<Grid container spacing={2} alignItems={"center"}>
							<Grid item xs={12}>
								<h3>{AppContext.r["tickets"]}</h3>
							</Grid>

							<Grid item xs={12} sm={3} md={3}>
								<FormControlLabel control={<Field component={FormikSwitch} type="checkbox" name="published" checked={values.is_free} />}
									label={AppContext.r["this-is-a-free-event"]} onChange={(e, checked) => {
										if (!checked && !store.has_stripe_setup) {
											layoutContext.toast.show({
												open: true, severity: ToastSeverityEnum.warning, autoHideDuration: 7000,
												content: <>
													<p>{AppContext.r["must-setup-stripe"]}</p>
													<Link to={"/" + store.subdomain + "/settings"}>{t('store-settings', { store: store.name })}</Link>
												</>
											});
										} else {
											setFieldValue("is_free", checked);
										}

										if (!checked)
											calculateFees(values.price);
									}} />
							</Grid>

							<Grid item xs={12} sm={9} md={9}>
								{values.is_free && store.licence &&
									<span>{AppContext.r["licence"]}: <b>{store.licence.title}</b> {" | " + store.licence.max_attendants_count + " " + AppContext.r["players"]}</span>}
							</Grid>

							{!values.is_free && store?.locale &&
								<>
									<Grid item xs={4} md={3}>
										<FormControl fullWidth sx={{ m: 1 }}>
											<InputLabel htmlFor="outlined-adornment-amount">{AppContext.r["price"]}</InputLabel>
											<OutlinedInput
												id="outlined-adornment-amount"
												value={values.price}
												onChange={(e) => {
													setFieldValue('price', (e.target['value']));
													calculateFees(parseFloat(e.target['value']));
												}}
												startAdornment={<InputAdornment position="start">{store?.locale?.currency_symbol}</InputAdornment>}
												label="Amount" />
										</FormControl>
									</Grid>

									<Grid item xs={4} md={3}>
										<div className="outlined-with-label transaction-fee">
											&nbsp;&nbsp;&nbsp;{store?.locale.currency_symbol + " " + transactionFee?.toFixed(2)}
											<label>{AppContext.r["transaction-fee"]}</label>
										</div>
									</Grid>

									<Grid item xs={4} md={3}>
										<div className="outlined-with-label you-earn">
											&nbsp;&nbsp;&nbsp;{store.locale.currency_symbol + " " + netIncome?.toFixed(2)}
											<label>{AppContext.r["you-earn"]}</label>
										</div>
									</Grid>

									{values.id &&
										<>
											{(!values.vouchers || values.vouchers.length === 0) &&
												<Grid item xs={12}>
													<ActionButton className="square"
														action={async () => {
															const response = await IqSpaceApi.GenerateFreeTickets(values);

															if (response && response.status === 201)
																setFieldValue("vouchers", response.data.data)
															else
																layoutContext.toast.show({
																	open: true, severity: ToastSeverityEnum.error, autoHideDuration: 5000,
																	content: "Error"
																});
														}}>
														&nbsp; {AppContext.r["generate-free-tickets"]}
													</ActionButton>
												</Grid>}

											{(values.vouchers && values.vouchers.length > 0) &&
												<Grid item xs={12}>
													<p>{AppContext.r["free-tickets-desc"]}</p>

													<div className="free-tickets">
														{values.vouchers.map(v =>
															<Chip disabled={v.updated_at} variant="outlined" label={v.code} icon={<Icon className="fas fa-ticket-alt" />} />)}
													</div>
												</Grid>}
										</>}
								</>}

							{values?.id && <EventExtraTabs event={values} store={store} />}

							<Grid item xs={12}>
								{values.id && (
									<>
										<DisplayCreatedAt createdAt={values.created_at} />
									</>
								)}
							</Grid>
						</Grid >}
				</Grid>

			</FormikProvider >
		);
	};

	const filtersForm = (_props: FormikProps<any>) => (
		<Form>
			<Grid container spacing={1}>
				<Grid item xs={12} sm={6} md={4}>
					<Field
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
						component={TextField}
						label={t('search')}
						size="small"
						name="search"
						type="text"
					/>
				</Grid>
			</Grid>
		</Form>
	);

	return (
		<Module
			ref={moduleRef}
			className='events'
			columns={columns}
			schema={schema}
			view={view}
			data={moduleData}
			initialValues={initialValues}
			filtersForm={filtersForm}
			initialFilters={{ store_id: store?.id, language_id: user?.language_id }}
			onItemLoaded={onItemLoaded}
		/>
	);
}

export default Events;